import { ReactNode, useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

import { useDragScroll } from '../model/use-drag-scroll';

/**
 * Scroll container that marks which horizontal edges currently hide
 * content (`data-scroll-start` / `data-scroll-end`), so pinned cells
 * show their edge separator only while something actually slides
 * underneath them — a table that fits stays shadow-free (MUI-like).
 *
 * Also pannable by pointer drag (`useDragScroll`), the only way a
 * mouse-without-trackpad user can reach columns hidden behind the
 * pinned actions cell.
 *
 * Private to data-table: the ResizeObserver is attached once to the
 * first child (the table wrapper), relying on React keeping that DOM
 * node stable across re-renders — don't render children whose first
 * element changes identity.
 */
export function TableScrollArea({ className, children }: { className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useDragScroll(ref);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      el.toggleAttribute('data-scroll-start', el.scrollLeft > 0);
      // 1px tolerance for fractional scroll positions
      el.toggleAttribute('data-scroll-end', el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    // content width changes too (columns toggled, data loaded)
    if (el.firstElementChild) resizeObserver.observe(el.firstElementChild);

    return () => {
      el.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        // Offer the grab handle exactly while there is something to pan to:
        // one of the two edge flags is set precisely when content overflows
        // horizontally. Buttons keep their own `cursor-pointer`.
        'data-[scroll-start]:cursor-grab data-[scroll-end]:cursor-grab',
        'data-[dragging]:cursor-grabbing data-[dragging]:select-none',
        className
      )}>
      {children}
    </div>
  );
}
